/**
 * Bet breakdown — Backend-UI `bet-breakdown` filter + table clone.
 * Filters: member (multi), From/To date, Currency, Max Row, Search, Clear Date,
 * Bet Count. No IP / Invalid Bet (Lab Aggregate / beta backend parity).
 */

import { useMemo, useState } from 'react'
import { Pager } from '@w7/ui'
import type { WorkstationBetFilters } from '@/api/hooks/net-exposure/useWorkstation'
import { money } from '@/lib/format'
import { betListMatchedUnmatchedAmt } from '@/features/net-exposure/betListMatchedAmt'
import { netExposureBookKindFromMarketType } from '@/features/net-exposure/netExposureDisplayCurrency'
import {
  formatFancyRuns,
  workstationOddsMatched,
  workstationOddsPlaced,
} from './lineSessionBetOdds'
import { WorkstationMemberSearch } from './WorkstationMemberSearch'
import {
  defaultBetDateRange,
  filterWorkstationBets,
  fromDateInputValue,
  localPartsFromIso,
  paginateBets,
  type WorkstationBet,
  type WorkstationBook,
} from '../workstation-fixture'

const CURRENCIES = ['All', 'INR', 'POINTS'] as const
/** Backend-UI workstation.js `$scope.maxRows` */
const PAGE_SIZES = [100, 200, 500, 1000] as const

function formatWhen(iso: string | null): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function toneClass(value: number): string {
  return value < 0 ? 'w7-ws-amt-neg' : 'w7-ws-amt-pos'
}

function showMatchedTime(marketName: string): boolean {
  return (
    marketName === 'Match Odds' ||
    marketName === 'Tied Match' ||
    marketName === 'Completed Match'
  )
}

export function BetBreakdown({
  book,
  surface = 'dark',
  live = false,
  betCount,
  betFilters,
  onBetFiltersChange,
}: {
  book: WorkstationBook
  surface?: 'dark' | 'light'
  live?: boolean
  betCount?: number
  betFilters?: WorkstationBetFilters
  onBetFiltersChange?: (next: WorkstationBetFilters) => void
}) {
  const serverBets = live && onBetFiltersChange
  const defaults = useMemo(() => defaultBetDateRange(), [])
  const memberOptions = useMemo(
    () => [...new Set(book.bets.map((b) => b.userName))].sort(),
    [book.bets],
  )

  const [memberNames, setMemberNames] = useState<string[]>([])
  const [memberDraft, setMemberDraft] = useState('')
  const [currency, setCurrency] = useState<string>('All')
  const [pageSize, setPageSize] = useState<number>(100)
  const [fromDate, setFromDate] = useState(defaults.fromDate)
  const [toDate, setToDate] = useState(defaults.toDate)
  const [page, setPage] = useState(1)
  const [applied, setApplied] = useState({
    memberNames: [] as string[],
    memberQuery: '',
    currency: 'All',
    fromDate: defaults.fromDate,
    toDate: defaults.toDate,
  })

  const matchedCol = showMatchedTime(book.marketName)

  const pushLiveFilters = (next: {
    memberNames?: string[]
    memberQuery?: string
    currency?: string
    fromDate?: string
    toDate?: string
    page?: number
    maxRow?: number
  }) => {
    if (!serverBets || !betFilters || !onBetFiltersChange) return
    const names = next.memberNames ?? applied.memberNames
    const query = next.memberQuery ?? applied.memberQuery
    const userName =
      names.length > 0 ? names : query.trim() ? query.trim() : undefined
    onBetFiltersChange({
      page: next.page ?? 1,
      maxRow: next.maxRow ?? pageSize,
      fromDate: next.fromDate ?? fromDate,
      toDate: next.toDate ?? toDate,
      currencyType: next.currency ?? currency,
      userName,
    })
  }

  const filtered = useMemo(
    () => (serverBets ? book.bets : filterWorkstationBets(book.bets, applied)),
    [serverBets, book.bets, applied],
  )

  const totalRows = serverBets ? (betCount ?? book.bets.length) : filtered.length
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize))
  const safePage = serverBets ? (betFilters?.page ?? 1) : Math.min(page, pageCount)
  const rows = serverBets ? book.bets : paginateBets(filtered, safePage, pageSize)

  const resetPage = () => setPage(1)

  const applyFilters = (next: {
    memberNames?: string[]
    memberQuery?: string
    currency?: string
    fromDate?: string
    toDate?: string
  }) => {
    setApplied((prev) => ({
      memberNames: next.memberNames ?? prev.memberNames,
      memberQuery: next.memberQuery ?? prev.memberQuery,
      currency: next.currency ?? prev.currency,
      fromDate: next.fromDate ?? prev.fromDate,
      toDate: next.toDate ?? prev.toDate,
    }))
    resetPage()
    if (serverBets) {
      pushLiveFilters({
        memberNames: next.memberNames ?? applied.memberNames,
        memberQuery: next.memberQuery ?? applied.memberQuery,
        currency: next.currency ?? applied.currency,
        fromDate: next.fromDate ?? applied.fromDate,
        toDate: next.toDate ?? applied.toDate,
      })
    }
  }

  /** Search — chips + free-text draft (Backend-UI getBetList('search')). */
  const runSearch = () => {
    const tag = memberDraft.trim()
    let names = memberNames
    if (tag && !memberNames.includes(tag)) {
      names = [...memberNames, tag]
      setMemberNames(names)
    }
    setMemberDraft('')
    applyFilters({
      memberNames: names,
      memberQuery: tag,
      currency,
      fromDate,
      toDate,
    })
  }

  const clearDates = () => {
    const next = defaultBetDateRange()
    setFromDate(next.fromDate)
    setToDate(next.toDate)
    applyFilters({ fromDate: next.fromDate, toDate: next.toDate })
  }

  const addMember = (name: string) => {
    if (!name || memberNames.includes(name)) return
    const names = [...memberNames, name]
    setMemberNames(names)
    applyFilters({ memberNames: names, memberQuery: '' })
  }

  const removeMember = (name: string) => {
    const names = memberNames.filter((n) => n !== name)
    setMemberNames(names)
    applyFilters({ memberNames: names })
  }

  const fixtureSuggestions = useMemo(
    () => memberOptions.filter((name) => !memberNames.includes(name)),
    [memberNames, memberOptions],
  )

  const bookKind = netExposureBookKindFromMarketType(book.type)
  const rowOffset = (safePage - 1) * pageSize

  return (
    <section className="w7-ws-bets" data-ws-surface={surface} aria-label="Bet breakdown">
      <div className="w7-ws-bets-toolbar">
        {/* Row 1 — member multi-select (Backend-UI ui-select + searchHierarchyNew) */}
        <WorkstationMemberSearch
          selected={memberNames}
          draft={memberDraft}
          onDraftChange={setMemberDraft}
          onAdd={addMember}
          onRemove={removeMember}
          fixtureSuggestions={fixtureSuggestions}
          onEnter={() => {
            if (memberDraft.trim()) {
              addMember(memberDraft.trim())
              setMemberDraft('')
            } else {
              runSearch()
            }
          }}
        />

        {/* Row 2 — dates, currency, max row, search */}
        <div className="w7-ws-bets-filters">
          <label className="w7-ws-bets-field">
            <span>From Date</span>
            <span className="w7-ws-bets-date">
              <input
                type="date"
                value={localPartsFromIso(fromDate)?.date ?? ''}
                onChange={(e) => {
                  const next = fromDateInputValue(e.target.value, false)
                  setFromDate(next)
                  applyFilters({ fromDate: next })
                }}
              />
            </span>
          </label>
          <label className="w7-ws-bets-field">
            <span>To Date</span>
            <span className="w7-ws-bets-date">
              <input
                type="date"
                value={localPartsFromIso(toDate)?.date ?? ''}
                onChange={(e) => {
                  const next = fromDateInputValue(e.target.value, true)
                  setToDate(next)
                  applyFilters({ toDate: next })
                }}
              />
            </span>
          </label>
          <label className="w7-ws-bets-field">
            <span>Currency Types</span>
            <select
              value={currency}
              onChange={(e) => {
                const next = e.target.value
                setCurrency(next)
                applyFilters({ currency: next })
              }}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="w7-ws-bets-field">
            <span>Max Row</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value)
                setPageSize(next)
                resetPage()
                if (serverBets) pushLiveFilters({ maxRow: next })
              }}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div className="w7-ws-bets-actions">
            <button type="button" className="w7-ws-bets-btn" onClick={runSearch}>
              Search
            </button>
          </div>
        </div>

        {/* Row 3 — clear / count */}
        <div className="w7-ws-bets-filters w7-ws-bets-filters-secondary">
          <button type="button" className="w7-ws-bets-btn" onClick={clearDates}>
            Clear Date
          </button>
          <div className="w7-ws-bets-count">Bet Count : {totalRows}</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="w7-ws-bets-empty">No Data Found</div>
      ) : (
        <div className="w7-ws-bets-scroll">
          <table className="w7-ws-bets-table">
            <thead>
              <tr>
                <th className="w7-ws-bets-check">#</th>
                <th>Member</th>
                <th>Place time</th>
                {matchedCol ? <th>Matched Time</th> : null}
                <th>Event</th>
                <th>Market</th>
                <th>Selection</th>
                <th>Odds Placed</th>
                <th>Odds Matched</th>
                <th>Matched</th>
                <th>Unmatched</th>
                <th>Profit/Liability</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((bet, i) => (
                <BetRow
                  key={bet.id}
                  index={rowOffset + i + 1}
                  bet={bet}
                  bookKind={bookKind}
                  matchedCol={matchedCol}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalRows > 0 ? (
        <div className="w7-ws-bets-foot">
          <Pager
            page={safePage}
            pageCount={pageCount}
            onChange={(nextPage) => {
              if (serverBets && betFilters && onBetFiltersChange) {
                onBetFiltersChange({ ...betFilters, page: nextPage })
              } else {
                setPage(nextPage)
              }
            }}
            total={totalRows}
            pageSize={pageSize}
          />
        </div>
      ) : null}
    </section>
  )
}

function BetRow({
  index,
  bet,
  bookKind,
  matchedCol,
}: {
  index: number
  bet: WorkstationBet
  bookKind: ReturnType<typeof netExposureBookKindFromMarketType>
  matchedCol: boolean
}) {
  return (
    <tr>
      <td className="w7-ws-bets-check">{index}</td>
      <td>
        {bet.userName} - {bet.currencyType}
      </td>
      <td>{formatWhen(bet.placedAt)}</td>
      {matchedCol ? <td>{formatWhen(bet.matchedAt)}</td> : null}
      <td title={bet.event}>{bet.event}</td>
      <td>{bet.marketName}</td>
      <td>
        <span className={`w7-ws-bet-side w7-ws-bet-side-${bet.type.toLowerCase()}`} />
        {bet.selectionName}
        {bet.runs != null ? ` ${formatFancyRuns(bet.runs)}` : ''}
      </td>
      <td>
        {workstationOddsPlaced({
          marketName: bet.marketName,
          betRate: bet.betRate,
          runs: bet.runs,
        })}
      </td>
      <td>
        {workstationOddsMatched({
          marketName: bet.marketName,
          runs: bet.runs,
          averagePriceMatched: bet.averagePriceMatched,
        })}
      </td>
      <td>{betListMatchedUnmatchedAmt(bet.matchedAmt, bet.currencyType, bookKind)}</td>
      <td>{betListMatchedUnmatchedAmt(bet.unMatchedAmt, bet.currencyType, bookKind)}</td>
      <td className={toneClass(bet.profitLiability)}>
        {money(bet.profitLiability, {
          round: true,
          symbol: false,
          currency: bet.currencyType,
        })}
      </td>
    </tr>
  )
}
